/**
 * Web-side call audio unlock and diagnostic logging for Zego WebRTC.
 */

const remoteLevelMonitors = new Map();

export function logWebLocalMicTrack(localStream, callId = null, role = null) {
  const tracks = localStream?.getAudioTracks?.() || [];
  const track = tracks[0] || null;
  console.log(
    `[webAudio] local mic track enabled=${!!track?.enabled} muted=${!!track?.muted} readyState=${track?.readyState || "none"}`,
    { callId, role, audioTracksLength: tracks.length }
  );
}

export function logWebRemoteAudioTracks(remoteStream, callId = null) {
  const tracks = remoteStream?.getAudioTracks?.() || [];
  if (!tracks.length) {
    console.log("[webAudio] remote audio track received (none)", { callId });
    return;
  }
  console.log("[webAudio] remote audio track received", {
    callId,
    count: tracks.length,
  });
  tracks.forEach((track, index) => {
    console.log(
      `[webAudio] remote audio track enabled=${!!track.enabled} muted=${!!track.muted} readyState=${track.readyState || "none"}`,
      { callId, index }
    );
    const logUnmute = () => {
      console.log("[webAudio] remote audio track unmuted", {
        callId,
        index,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      });
    };
    if (typeof track.addEventListener === "function") {
      track.addEventListener("unmute", logUnmute);
    } else {
      track.onunmute = logUnmute;
    }
  });
}

export async function unlockWebCallAudio() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        await ctx.resume();
        console.log("[webAudio] resume AudioContext success");
      }
      await ctx.close();
    }
  } catch (e) {
    console.warn("[webAudio] resume AudioContext failed", e?.message || e);
  }

  try {
    const el = document.createElement("audio");
    el.setAttribute("playsinline", "true");
    el.muted = true;
    el.volume = 0;
    el.src =
      "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
    await el.play();
    el.pause();
    console.log("[webAudio] autoplay unlock success");
  } catch (e) {
    console.warn("[webAudio] autoplay unlock failed", e?.message || e);
  }
}

export function ensureWebPublishAudioUnmuted(zg) {
  try {
    if (zg?.mutePublishStreamAudio) {
      zg.mutePublishStreamAudio(false);
    }
  } catch (e) {
    console.warn("[webAudio] mutePublishStreamAudio failed", e?.message || e);
  }
}

export function ensureWebPlayAudioUnmuted(zg, streamID) {
  if (!streamID) return;
  try {
    if (zg?.mutePlayStreamAudio) {
      zg.mutePlayStreamAudio(streamID, false);
    }
  } catch (e) {
    console.warn("[webAudio] mutePlayStreamAudio failed", e?.message || e);
  }
}

function computeLevelStats(analyser, buffer) {
  analyser.getFloatTimeDomainData(buffer);
  let sumSq = 0;
  let peak = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const sample = buffer[i];
    sumSq += sample * sample;
    const abs = Math.abs(sample);
    if (abs > peak) peak = abs;
  }
  const rms = Math.sqrt(sumSq / buffer.length);
  return { rms, peak };
}

/**
 * Measure remote stream RMS/peak every 1s for 15s after attach.
 */
export function startWebRemoteAudioLevelMonitor(remoteStream, streamID, callId = null) {
  if (!remoteStream || !streamID) return;

  stopWebRemoteAudioLevelMonitor(streamID);

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    console.warn("[webAudio] remote audio level monitor unavailable (no AudioContext)", {
      streamID,
      callId,
    });
    return;
  }

  let ctx;
  let source;
  let analyser;
  let buffer;
  let intervalId = null;
  let tick = 0;
  let silentTicks = 0;
  const maxTicks = 15;
  const silentRmsThreshold = 0.001;

  try {
    ctx = new AudioCtx();
    source = ctx.createMediaStreamSource(remoteStream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    buffer = new Float32Array(analyser.fftSize);
    source.connect(analyser);
    void ctx.resume();
  } catch (e) {
    console.warn("[webAudio] remote audio level monitor setup failed", {
      streamID,
      callId,
      error: e?.message || e,
    });
    return;
  }

  intervalId = window.setInterval(() => {
    tick += 1;
    const { rms, peak } = computeLevelStats(analyser, buffer);
    console.log(
      `[webAudio] remote audio level streamID=${streamID} rms=${rms.toFixed(5)} peak=${peak.toFixed(5)}`,
      { callId, tick }
    );

    if (rms < silentRmsThreshold) {
      silentTicks += 1;
    }

    if (tick >= maxTicks) {
      if (silentTicks >= maxTicks - 1) {
        console.warn("[webAudio] remote audio appears silent from native source", {
          streamID,
          callId,
          silentTicks,
          maxTicks,
        });
      }
      stopWebRemoteAudioLevelMonitor(streamID);
    }
  }, 1000);

  remoteLevelMonitors.set(streamID, { intervalId, ctx, source, analyser });
}

export function stopWebRemoteAudioLevelMonitor(streamID) {
  const handle = remoteLevelMonitors.get(streamID);
  if (!handle) return;

  if (handle.intervalId != null) {
    window.clearInterval(handle.intervalId);
  }
  try {
    handle.source?.disconnect();
  } catch (_) {}
  try {
    handle.analyser?.disconnect();
  } catch (_) {}
  void handle.ctx?.close();
  remoteLevelMonitors.delete(streamID);
}

export function logWebRemoteAudioAttached(streamID, callId = null, remoteStream = null) {
  console.log("[webAudio] remote audio attached/playing", { streamID, callId });
  if (remoteStream) {
    startWebRemoteAudioLevelMonitor(remoteStream, streamID, callId);
  }
}
